import type { AxiosInstance, AxiosRequestConfig } from 'axios';

import type { EndpointDef, PathParamsOf, RequestOf, ResponseOf } from '@d3/contract';

import { emitSessionExpired, getAccessToken, resetSession, sessionStore } from '@shared/session';

import { API_RETRY, IDEMPOTENCY_KEY_HEADER, REQUEST_ID_HEADER } from './api.constant';
import { type ApiError, CLIENT_ERROR_CODE, isApiError, normalizeError } from './api-error';
import { fillPath } from './endpoint-path';
import { rawClient } from './raw-client';
import { createRequestId } from './request-id';
import { whenSessionRestored } from './session-restore';
import { type RefreshOutcome, refreshSession } from './token-refresh';

/**
 * 공용 HTTP client. 책임은 네 가지뿐이다:
 * 인증 헤더 부착 · 토큰 갱신 · 에러 정규화 · 타임아웃/취소. **캐시는 두지 않는다.**
 *
 * 화면 · hook 은 axios 를 직접 부르지 않고 계약의 엔드포인트 정의로 `api.request` 를 부른다.
 */
type HasKeys<T> = [keyof T] extends [never] ? false : true;

type ParamsOption<E extends EndpointDef> =
  HasKeys<PathParamsOf<E>> extends true ? { params: PathParamsOf<E> } : { params?: undefined };

type PayloadOption<E extends EndpointDef> =
  RequestOf<E> extends undefined
    ? { query?: undefined; body?: undefined }
    : E['method'] extends 'GET' | 'DELETE'
      ? { query?: RequestOf<E>; body?: undefined }
      : { body: RequestOf<E>; query?: undefined };

export type RequestOptions<E extends EndpointDef> = ParamsOption<E> &
  PayloadOption<E> & {
    signal?: AbortSignal;
    timeoutMs?: number;
    /** 변경 요청을 재시도해도 안전할 때만 준다. 주면 네트워크 오류 · 5xx 에 재시도한다 */
    idempotencyKey?: string;
    /** 파일 내려받기처럼 바디가 JSON 이 아닐 때. 주면 응답을 Blob 으로 받는다(엔드포인트의 res 도 Blob) */
    responseType?: 'blob';
  };

type RequestArgs<E extends EndpointDef> =
  HasKeys<PathParamsOf<E>> extends true
    ? [options: RequestOptions<E>]
    : RequestOf<E> extends undefined
      ? [options?: RequestOptions<E>]
      : E['method'] extends 'GET' | 'DELETE'
        ? [options?: RequestOptions<E>]
        : [options: RequestOptions<E>];

interface LooseOptions {
  params?: Readonly<Record<string, string | number>>;
  query?: unknown;
  body?: unknown;
  signal?: AbortSignal;
  timeoutMs?: number;
  idempotencyKey?: string;
  responseType?: 'blob';
}

export interface HttpClientDeps {
  transport: AxiosInstance;
  whenReady: () => Promise<void>;
  getAccessToken: () => string | null;
  refresh: () => Promise<RefreshOutcome>;
  /** 갱신이 거절됐을 때(세션 종료) */
  onSessionRejected: () => void;
  /** 서버가 점검 코드를 줬을 때 */
  onMaintenance: () => void;
  wait: (ms: number) => Promise<void>;
}

function isRetryable(error: ApiError): boolean {
  if (isApiError(error, [CLIENT_ERROR_CODE.NETWORK_ERROR, CLIENT_ERROR_CODE.TIMEOUT])) return true;
  if (isApiError(error, 'SERVICE_UNAVAILABLE')) return false;
  return error.status >= 500;
}

export function createHttpClient(deps: HttpClientDeps) {
  async function send<T>(
    endpoint: EndpointDef,
    options: LooseOptions,
    token: string | null,
  ): Promise<T> {
    const config: AxiosRequestConfig = {
      method: endpoint.method,
      url: fillPath(endpoint.path, options.params),
      headers: {
        [REQUEST_ID_HEADER]: createRequestId(),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.idempotencyKey ? { [IDEMPOTENCY_KEY_HEADER]: options.idempotencyKey } : {}),
      },
      ...(options.query !== undefined ? { params: options.query } : {}),
      ...(options.body !== undefined ? { data: options.body } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
      ...(options.timeoutMs !== undefined ? { timeout: options.timeoutMs } : {}),
      ...(options.responseType ? { responseType: options.responseType } : {}),
    };
    try {
      const response = await deps.transport.request<T>(config);
      return response.data;
    } catch (caught) {
      throw normalizeError(caught);
    }
  }

  async function sendWithRefresh<T>(endpoint: EndpointDef, options: LooseOptions): Promise<T> {
    const usedToken = deps.getAccessToken();
    try {
      return await send<T>(endpoint, options, usedToken);
    } catch (caught) {
      const error = normalizeError(caught);
      // 토큰은 유효한데 세션이 폐기됐다(다른 탭 · 기기에서 로그아웃, 비밀번호 변경, 비활성화):
      // 그 요청만 실패시키지 않고 세션을 끝내 로그인 화면으로 보낸다(DD-48 #1 — 구·신 비교 D-14)
      if (usedToken !== null && isApiError(error, 'UNAUTHORIZED_SESSION_INVALID')) {
        deps.onSessionRejected();
        throw error;
      }
      // 서명 오류(UNAUTHORIZED)도 토큰을 보냈을 때는 만료와 같이 한 번 갱신한다: 서버의 액세스 키 교체 ·
      // 재배포로 옛 토큰의 서명만 맞지 않게 됐고 세션 쿠키는 아직 유효하다(DD-88).
      // 토큰 없이 받은 UNAUTHORIZED 는 갱신할 세션이 없으므로 그대로 실패시킨다.
      const refreshable =
        isApiError(error, 'UNAUTHORIZED_TOKEN_EXPIRED') ||
        (usedToken !== null && isApiError(error, 'UNAUTHORIZED'));
      if (!refreshable) throw error;

      // 이 요청이 떠 있는 동안 다른 요청이 이미 갱신을 끝냈다. 다시 갱신하지 않고 새 토큰으로 보낸다
      // (리프레시 토큰이 회전하면 불필요한 갱신이 재사용 탐지에 걸린다).
      const currentToken = deps.getAccessToken();
      if (currentToken !== null && currentToken !== usedToken) {
        return send<T>(endpoint, options, currentToken);
      }

      const outcome = await deps.refresh();
      if (outcome.result === 'ok') {
        // 갱신 뒤 한 번만 다시 보낸다. 또 만료 · 서명 오류가 오면 그대로 실패시킨다(무한 반복 방지).
        return send<T>(endpoint, options, deps.getAccessToken());
      }
      if (outcome.result === 'rejected') {
        deps.onSessionRejected();
        throw error;
      }
      // unreachable: 세션을 유지하고 이 요청만 실패시킨다.
      throw outcome.error;
    }
  }

  async function request<E extends EndpointDef>(
    endpoint: E,
    ...args: RequestArgs<E>
  ): Promise<ResponseOf<E>> {
    const options = (args[0] ?? {}) as LooseOptions;
    await deps.whenReady();

    const canRetry = endpoint.method === 'GET' || Boolean(options.idempotencyKey);

    for (let attempt = 0; ; attempt += 1) {
      try {
        return await sendWithRefresh<ResponseOf<E>>(endpoint, options);
      } catch (caught) {
        const error = normalizeError(caught);
        if (isApiError(error, 'SERVICE_UNAVAILABLE')) deps.onMaintenance();

        const aborted = options.signal?.aborted === true;
        if (!canRetry || aborted || attempt >= API_RETRY.maxRetries || !isRetryable(error)) {
          throw error;
        }
        await deps.wait(API_RETRY.baseDelayMs * API_RETRY.factor ** attempt);
      }
    }
  }

  return { request };
}

export type HttpClient = ReturnType<typeof createHttpClient>;

/** 세션이 끝났다: 사용자 상태를 비우고 알린다(app 이 로그인 화면으로 보낸다). */
export function expireSession(): void {
  resetSession();
  emitSessionExpired();
}

export const api: HttpClient = createHttpClient({
  transport: rawClient,
  whenReady: whenSessionRestored,
  getAccessToken,
  refresh: () => refreshSession(),
  onSessionRejected: expireSession,
  onMaintenance: () => sessionStore.getState().setMaintenance(),
  wait: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
});
